import LanguageToggle from './LanguageToggle.jsx';
import { useLanguage } from './LanguageProvider.jsx';
import { Archive, Boxes, CalendarDays, Percent, ChartNoAxesCombined, FileChartColumn, Home, Landmark, LayoutGrid, LogOut, Package, ReceiptText, RefreshCw, Settings, ShieldCheck, Scale, ShoppingCart, Tags, Truck, UserCog, Users, Wallet, Wifi, WifiOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import InstallApp from './InstallApp.jsx';
import ThemeToggle from './ThemeToggle.jsx';

/* كل تبويب: [المفتاح، الاسم الكامل، الأيقونة، الاسم المختصر لعمود التنقّل]
   الاسم الكامل يبقى في شريط الأوامر وفي لوحة «كل الأقسام» — المختصر للعمود الضيق فقط. */
export default function AppShell({ user, view, onNavigate, onLogout, syncStatus, children }) {
  const { t } = useLanguage();
  const adminGroups = [
    { title: t("nav.home"), items: [
      ['dashboard',t("nav.overview"),Home,t("nav.home")],
    ]},
    { title: t("nav.salesCustomers"), items: [
      ['daily-sales',t("nav.dailySales"),ChartNoAxesCombined,t("nav.sales")],
      ['customers',t("nav.customerFiles"),Users,t("nav.customers")],
      ['legacy-sales',t("nav.legacySales"),Archive,t("nav.legacy")],
    ]},
    { title: t("nav.productsInventory"), items: [
      ['products',t("nav.productFiles"),Package,t("nav.products")],
      ['inventory',t("nav.inventoryCount"),Boxes,t("nav.inventory")],
      ['purchases',t("nav.purchases"),ShoppingCart,t("nav.purchases")],
      ['expenses',t("nav.expensesFull"),Wallet,t("nav.expenses")],
      ['suppliers',t("nav.suppliers"),Truck,t("nav.suppliers")],
      ['pricing',t("nav.pricingFull"),Tags,t("nav.pricing")],
      ['margins',t("nav.marginsFull"),Percent,t("nav.margins")],
      ['date-labels',t("nav.dateLabels"),CalendarDays,t("nav.labels")],
    ]},
    { title: t("nav.reportsAdmin"), items: [
      ['pnl',t("nav.pnl"),Scale,t("nav.profit")],
      ['vat-return',t("nav.vatReturn"),Landmark,t("nav.tax")],
      ['reports',t("nav.reports"),FileChartColumn,t("nav.reports")],
      ['employees',t("nav.employeesFull"),UserCog,t("nav.employees")],
      ['audit',t("nav.auditFull"),ShieldCheck,t("nav.audit")],
      ['account',t("nav.accountFull"),Settings,t("nav.account")],
    ]},
  ];
  // الكاشير: البيع وطباعة ملصقات التواريخ + إعدادات حسابه. لا شاشات إدارية ولا حذف.
  const cashierGroups = [
    { title: t("nav.selling"), items: [
      ['pos',t("nav.pos"),ReceiptText,t("nav.selling")],
      ['date-labels',t("nav.dateLabels"),CalendarDays,t("nav.labels")],
    ]},
    { title: t("nav.account"), items: [
      ['account',t("nav.accountFull"),Settings,t("nav.account")],
    ]},
  ];
  // تبويبات الإبهام على الجوال — الأكثر استخداماً، والبقية داخل «المزيد»
  const adminTabs = ['dashboard','daily-sales','products','reports'];
  const cashierTabs = ['pos','date-labels','account'];


  const [sheetOpen,setSheetOpen]=useState(false);
  const groups=user.role==='admin'?adminGroups:cashierGroups;
  const flat=groups.flatMap(g=>g.items);
  const active=flat.find(([key])=>key===view) || flat[0];
  const activeGroup=groups.find(g=>g.items.some(([key])=>key===active[0]))?.title || '';
  const ActiveIcon=active[2];
  const tabKeys=user.role==='admin'?adminTabs:cashierTabs;
  const tabs=tabKeys.map(k=>flat.find(([key])=>key===k)).filter(Boolean);
  const navigate=(key)=>{onNavigate(key);setSheetOpen(false);};
  const roleLabel=user.role==='admin'?t("role.admin"):t("role.cashier");
  const syncLabel=syncStatus==='connected'?t("sync.connected"):syncStatus==='offline'?t("sync.offline"):t("sync.connecting");

  // Esc يغلق لوحة الأقسام
  useEffect(()=>{
    if(!sheetOpen) return;
    const onKey=(e)=>{ if(e.key==='Escape') setSheetOpen(false); };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[sheetOpen]);

  return <div className={`app-shell app-shell--${user.role}`}>
    {/* عمود التنقّل — أيقونات باسم مختصر، ثابت على الشاشات الكبيرة */}
    <aside className="sidebar">
      <a className="rail__brand" href="#top" onClick={(e)=>{e.preventDefault();navigate(flat[0][0]);}} aria-label={t("brand.name")}>
        <img src="/bait-henna-logo.jpg" alt="" />
      </a>
      <nav className="rail__nav" aria-label={t("nav.main")}>
        {groups.map(group=>(
          <div key={group.items[0][0]} className="rail__group" role="group" aria-label={group.title}>
            {group.items.map(([key,label,Icon,short])=>(
              <button key={key} className={view===key?'rail__item active':'rail__item'}
                onClick={()=>navigate(key)} title={label} aria-label={label} aria-current={view===key?'page':undefined}>
                <Icon /><span>{short}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div className="rail__foot">
        <button className="rail__item" onClick={()=>setSheetOpen(true)} title={t("nav.all")}>
          <LayoutGrid /><span>{t("nav.sections")}</span>
        </button>
        <button className="rail__item rail__item--out" onClick={onLogout} title={t("nav.logout")}>
          <LogOut /><span>{t("nav.out")}</span>
        </button>
      </div>
    </aside>

    <main className="workspace">
      <header className="topbar">
        <button className="topbar__mark" onClick={()=>setSheetOpen(true)} aria-label={t("nav.open")}>
          <img src="/bait-henna-logo.jpg" alt={t("brand.name")} />
        </button>
        <div className="topbar__title">
          <ActiveIcon />
          <span>
            <small>{activeGroup}</small>
            <strong>{active[1]}</strong>
          </span>
        </div>
        <div className="topbar__tools">
          <span className={`sync-state sync-state--${syncStatus}`} title={syncLabel}>
            {syncStatus==='connected'?<Wifi/>:syncStatus==='offline'?<WifiOff/>:<RefreshCw/>}
            <span>{syncLabel}</span>
          </span>
          <InstallApp/>
          <LanguageToggle/><ThemeToggle/>
          <button className="profile" onClick={()=>setSheetOpen(true)} aria-label={t("nav.profile")}>
            <span className="avatar">{user.name.slice(0,1)}</span>
            <span><strong><bdi>{user.name}</bdi></strong><small>{roleLabel}</small></span>
          </button>
        </div>
      </header>
      <div className="page">{children}</div>
    </main>

    {/* شريط الإبهام — بديل القائمة المنسدلة على الجوال */}
    <nav className="tabbar" aria-label={t("nav.quick")}>
      {tabs.map(([key,label,Icon,short])=>(
        <button key={key} className={view===key?'active':''} onClick={()=>navigate(key)} aria-current={view===key?'page':undefined}>
          <Icon /><span>{short}</span>
        </button>
      ))}
      <button className={sheetOpen?'active':''} onClick={()=>setSheetOpen(true)}>
        <LayoutGrid /><span>{t("nav.more")}</span>
      </button>
    </nav>

    {sheetOpen?<div className="sheet-backdrop" onClick={()=>setSheetOpen(false)}>
      <div className="sheet" role="dialog" aria-label={t("nav.all")} onClick={(e)=>e.stopPropagation()}>
        <header className="sheet__head">
          <span className="sheet__user">
            <span className="avatar">{user.name.slice(0,1)}</span>
            <span><strong><bdi>{user.name}</bdi></strong><small>{roleLabel}</small></span>
          </span>
          <button className="icon-button" onClick={()=>setSheetOpen(false)} aria-label={t("common.close")}><X /></button>
        </header>
        <div className="sheet__body">
          {groups.map(group=>(
            <section key={group.items[0][0]} className="sheet__group">
              <h3>{group.title}</h3>
              <div>
                {group.items.map(([key,label,Icon])=>(
                  <button key={key} className={view===key?'active':''} onClick={()=>navigate(key)}>
                    <Icon /><span>{label}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
        <footer className="sheet__foot">
          <button className="button button--outline" onClick={onLogout}><LogOut />{t("nav.logout")}</button>
        </footer>
      </div>
    </div>:null}
  </div>;
}
